import { Maybe, PrismicImageType } from '@emico/graphql-schema-types'

import {
    MonstersalePageBodyProductVideoHero,
    StorelocatorPageBodyHero,
} from './graphql/schema.generated'
import { TweakwiseConfigurator } from './guided-selling/TweakwiseConfigurator'
import Header from './layout/Header'
import HeaderContainer from './layout/Header/HeaderContainer'
import PageWrapper from './layout/PageWrapper'
import NotFoundPage from './NotFoundPage'
import PageLoader from './presentation/PageLoader'
import PrismicCmsFaqSlices from './PrismicCmsFaqSlices'
import PrismicCmsSlices from './PrismicCmsSlices'
import PrismicGrid from './PrismicGrid'
import PrismicHelmet from './PrismicHelmet'
import styles from './PrismicPage.module.scss'
import PrismicSliceHero from './PrismicSliceHero'
import PrismicSliceProductHero from './PrismicSliceProductHero'
import usePrismicPage from './usePrismicPage'
import getLayoutStyle from './utils/getLayoutStyle'

interface Props {
    urlKey: string
    prismicType: string
}

const heros = [
    'PageBodyHero',
    'VanguardPageBodyHero',
    'CastironPageBodyHero',
    'JustbrandsPageBodyHero',
    'JbfoPageBodyHero',
    'MonstersalePageBodyHero',
    'PmeOutletPageBodyHero',
    'FaqPageBodyHero',
    'VanguardFaqPageBodyHero',
    'CastironFaqPageBodyHero',
    'JustbrandsFaqPageBodyHero',
    'JbfoFaqPageBodyHero',
    'MonstersaleFaqPageBodyHero',
    'PmeOutletFaqPageBodyHero',
    'StorelocatorPageBodyHero',
]

const productHeros = [
    'PageBodyProductHero',
    'VanguardPageBodyProductHero',
    'CastironPageBodyProductHero',
    'JustbrandsPageBodyProductHero',
    'MonstersalePageBodyProductVideoHero',
    'JbfoPageBodyProductVideoHero',
    'PmeOutletPageBodyProductHero',
]

const PrismicPage = ({ urlKey, prismicType }: Props) => {
    let loading = false
    let prismicPage

    try {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { data, loading: prismicLoading } = usePrismicPage(
            urlKey,
            prismicType,
        )

        prismicPage = data
        loading = prismicLoading
    } catch {
        // Catch issues with incorrect prismic configuration
        return <NotFoundPage />
    }

    if (loading) {
        return (
            <PageWrapper pageType="cms">
                <PageLoader fullScreen reason="Loading prismic data..." />
            </PageWrapper>
        )
    }

    if (!prismicPage?.body) {
        return <NotFoundPage />
    }

    const style = getLayoutStyle(prismicPage?.style)
    const hideLayout = 'hideLayout' in prismicPage && prismicPage.hideLayout

    const firstSlice = prismicPage.body[0]
    const firstIsHero =
        firstSlice?.__typename && heros.includes(firstSlice.__typename)
    const firstIsProductHero =
        firstSlice?.__typename && productHeros.includes(firstSlice.__typename)

    const slicesExcludingHero =
        firstIsHero || firstIsProductHero
            ? prismicPage.body.slice(1)
            : prismicPage.body

    return (
        <PageWrapper
            pageType="cms"
            header={
                hideLayout ? null : (
                    <HeaderContainer>
                        <Header />
                    </HeaderContainer>
                )
            }
            footer={hideLayout ? null : undefined}
            className={styles[style]}
        >
            <PrismicHelmet page={prismicPage} />

            {prismicPage.__typename === 'Page' &&
            prismicPage.tweakwiseFunnelId ? (
                <TweakwiseConfigurator
                    theme={prismicPage?.style ?? ''}
                    tweakwiseFunnelId={prismicPage.tweakwiseFunnelId}
                />
            ) : (
                <>
                    {firstIsHero && prismicPage.body[0] && (
                        <PrismicSliceHero
                            // Types are checked earlier
                            slice={firstSlice as StorelocatorPageBodyHero}
                        />
                    )}
                    {firstIsProductHero && prismicPage.body[0] && (
                        <PrismicSliceProductHero
                            slice={
                                firstSlice as MonstersalePageBodyProductVideoHero
                            }
                        />
                    )}
                    <PrismicGrid
                        pageBackground={
                            'pageBackground' in prismicPage
                                ? (prismicPage.pageBackground as
                                      | Maybe<PrismicImageType>
                                      | undefined)
                                : undefined
                        }
                        pageBackgroundColor={
                            'pageBackgroundColor' in prismicPage
                                ? (prismicPage.pageBackgroundColor as
                                      | Maybe<'light' | 'dark'>
                                      | undefined)
                                : undefined
                        }
                    >
                        {prismicType.endsWith('faq_page') ? (
                            <PrismicCmsFaqSlices
                                slices={slicesExcludingHero}
                                urlKey={prismicPage.urlKey || ''}
                                style={style}
                            />
                        ) : (
                            <PrismicCmsSlices
                                slices={slicesExcludingHero}
                                prismicType={prismicType}
                            />
                        )}
                    </PrismicGrid>
                </>
            )}
        </PageWrapper>
    )
}

export default PrismicPage
